/**
 * <ThinkingBlock> — collapsed disclosure for the model's extended thinking.
 * Hidden by default to preserve scanability; click to expand the raw text.
 *
 * ## The label is doing legal-product work, not decoration (D3)
 *
 * This is the model reasoning ALOUD over case content — a draft, not advice,
 * and not a conclusion the lawyer should act on. D3 fixes the framing as
 * *"rascunho de raciocínio"* precisely so the register says so before the
 * content does.
 *
 * ⚠ Until 2026-08-13 this rendered a hardcoded English `"Reasoning"`, which
 * both broke locale parity on an otherwise fully-localised surface AND silently
 * dropped that framing. Keep it localised; if you are tempted to inline a
 * string here again, the ruling is what you would be discarding.
 */
import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ThinkingBlockProps {
  text: string;
  /** `true` while the turn is still producing this thinking. Live blocks get a
   *  gentler affordance; nothing about persistence changes — thinking is
   *  EPHEMERAL either way (D3, see `stores/useThinkingStore.ts`). */
  live?: boolean;
}

export function ThinkingBlock({ text, live = false }: ThinkingBlockProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!text.trim()) return null;
  return (
    <div
      data-testid="thinking-block"
      data-live={live || undefined}
      className="my-1 rounded-md border border-cream-300 bg-cream-100/30 text-body-sm dark:border-coffee-700 dark:bg-coffee-900/30"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 px-3 py-1.5 text-caption font-medium uppercase tracking-wide text-coffee-600 dark:text-cream-400"
        aria-expanded={open}
      >
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <span>{t("chat.thinking.label")}</span>
      </button>
      {open && (
        <div
          data-testid="thinking-block-body"
          className="whitespace-pre-wrap border-t border-cream-300 px-3 py-2 font-reading text-coffee-700 dark:border-coffee-700 dark:text-cream-300"
        >
          {text}
        </div>
      )}
    </div>
  );
}
